import { Injectable } from '@angular/core';
import { CookieRepository, CookieOptions } from '@service/cookie.repository';

/**
 * Implementación del servicio de gestión de cookies
 * Proporciona métodos para CRUD de cookies del navegador
 */
@Injectable({
  providedIn: 'root'
})
export class CookieServiceImpl extends CookieRepository {
  /**
   * Establece una cookie con sus opciones
   * @param name Nombre de la cookie
   * @param value Valor de la cookie
   * @param options Opciones adicionales
   */
  setCookie(name: string, value: string, options?: CookieOptions): void {
    let cookieString = `${this.encodeName(name)}=${this.encodeValue(value)}`;

    if (options?.path) {
      cookieString += `; path=${options.path}`;
    } else {
      cookieString += '; path=/';
    }

    if (options?.domain) {
      cookieString += `; domain=${options.domain}`;
    }

    if (options?.expires) {
      const expiresDate =
        options.expires instanceof Date
          ? options.expires
          : new Date(options.expires);
      cookieString += `; expires=${expiresDate.toUTCString()}`;
    }

    if (options?.maxAge !== undefined) {
      cookieString += `; max-age=${options.maxAge}`;
    }

    if (options?.secure) {
      cookieString += '; secure';
    }

    if (options?.sameSite) {
      cookieString += `; samesite=${options.sameSite}`;
    }

    document.cookie = cookieString;

    // Log con información de auditoría
    this.logCookieAction('SET', name, options);
  }

  /**
   * Log estructurado de acciones en cookies con auditoría
   * @private
   */
  private logCookieAction(
    action: 'SET' | 'GET' | 'DELETE',
    cookieName: string,
    options?: CookieOptions
  ): void {
    const auditInfo = {
      action,
      cookieName,
      timestamp: new Date().toISOString(),
      userId: options?.userId || 'anonymous',
      userName: options?.userName || 'unknown',
      userAgent: options?.userAgent || navigator.userAgent?.substring(0, 50),
      expiration: options?.expires
        ? new Date(
            options.expires instanceof Date
              ? options.expires.getTime()
              : options.expires
          ).toISOString()
        : undefined,
      metadata: options?.metadata
    };

    console.log(
      `%c[${action}] Cookie: ${cookieName}`,
      'color: #2196F3; font-weight: bold;',
      auditInfo
    );
  }

  /**
   * Obtiene el valor de una cookie
   * @param name Nombre de la cookie
   * @returns Valor de la cookie o undefined
   */
  getCookie(name: string): string | undefined {
    const encodedName = this.encodeName(name);
    const cookies = document.cookie.split(';');

    for (const cookie of cookies) {
      const trimmed = cookie.trim();
      if (trimmed.startsWith(encodedName + '=')) {
        return this.decodeValue(trimmed.substring(encodedName.length + 1));
      }
    }

    return undefined;
  }

  /**
   * Elimina una cookie específica
   * @param name Nombre de la cookie
   */
  deleteCookie(name: string): void {
    this.setCookie(name, '', {
      maxAge: -1,
      path: '/'
    });
  }

  /**
   * Obtiene todas las cookies como objeto
   * @returns Objeto con todas las cookies
   */
  getAllCookies(): Record<string, string> {
    const cookies: Record<string, string> = {};
    const allCookies = document.cookie.split(';');

    for (const cookie of allCookies) {
      const [name, value] = cookie.trim().split('=');
      if (name) {
        cookies[this.decodeName(name)] = this.decodeValue(value);
      }
    }

    return cookies;
  }

  /**
   * Elimina todas las cookies
   */
  deleteAllCookies(): void {
    const cookies = this.getAllCookies();
    Object.keys(cookies).forEach((name) => {
      this.deleteCookie(name);
    });
  }

  /**
   * Verifica si une cookie existe
   * @param name Nombre de la cookie
   * @returns true si existe
   */
  exists(name: string): boolean {
    return this.getCookie(name) !== undefined;
  }

  /**
   * Codifica el nombre de la cookie (reemplaza caracteres especiales)
   * @private
   */
  private encodeName(name: string): string {
    return encodeURIComponent(name);
  }

  /**
   * Decodifica el nombre de la cookie
   * @private
   */
  private decodeName(name: string): string {
    try {
      return decodeURIComponent(name);
    } catch {
      return name;
    }
  }

  /**
   * Codifica el valor de la cookie
   * @private
   */
  private encodeValue(value: string): string {
    return encodeURIComponent(value);
  }

  /**
   * Decodifica el valor de la cookie
   * @private
   */
  private decodeValue(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
}
